
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { VisibilityRounded } from "@mui/icons-material";

import { Alert, Dialog, Table } from "../../layout";

import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { DropLi } from "../../hooks";
import { AppContext } from "../../App";
import { formatCurrency } from "../../hooks/Currency";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Employee Details
//------------------------------------------------
const EmployeeDetailsContent = ({
  employee,
  t,
}) => {
  const fields = [
    {
      label: t("firstName"),
      value: employee?.firstName,
    },
    {
      label: t("lastName"),
      value: employee?.lastName,
    },
    {
      label: t("dateOfBirth"),
      value: employee?.dob,
    },
    {
      label: t("salaryType"),
      value: employee?.salaryType,
    },
    {
      label: t("salary"),
      value: employee?.salaryDisplay,
    },
    {
      label: t("hireDate"),
      value: employee?.addDate,
    },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("employeeDetails")}
      </Typography>

      {fields.map((field, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                minWidth: "35%",
              }}
            >
              {field.label}:
            </Typography>

            <Typography
              sx={{
                textAlign: "right",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {field.value === null ||
              field.value === undefined ||
              field.value === ""
                ? "-"
                : field.value}
            </Typography>
          </Box>

          <Divider />
        </React.Fragment>
      ))}
    </Box>
  );
};

//------------------------------------------------
// Employee History
//------------------------------------------------
const EmployeeHistory = (props) => {
  const { t } = useTranslation();

  const {
    _clear,
    _get,
    _list,
    _type,
    _store,
  } = props;

  const { Periodic } = DropLi();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Employees
  //------------------------------------------------
  useEffect(() => {
    setLoader(true);

    _get(
      endpoints.User + controller.GetAllEmployees,
      actions.ActionType.List,
      () => setLoader(false)
    );

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //------------------------------------------------
  // Employee Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (
      _type !== "employees" ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => {
      const salaryTypeName =
        Periodic.find(
          (item) => item.id === obj.salaryType
        )?.name || "";

      const salaryDisplay =
        obj.salary === null ||
        obj.salary === undefined ||
        obj.salary === ""
          ? "-"
          : formatCurrency(Number(obj.salary), _store);

      return {
        ...obj,
        salaryType: salaryTypeName,
        salaryDisplay,
      };
    });
  }, [_list, _type, Periodic, _store]);

  //------------------------------------------------
  // View Employee
  //------------------------------------------------
  const handleView = (data) => {
    const employee = tableData.find(
      (item) => Number(item.id) === Number(data.id)
    );

    setViewData(employee || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setViewData(null);
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "firstName", label: t("firstName") },
    { id: "lastName", label: t("lastName") },
    { id: "dob", label: t("dateOfBirth") },
    { id: "salaryType", label: t("salaryType") },
    { id: "salary", label: t("salary") },
    { id: "addDate", label: t("hireDate") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("employeeHistory")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("employeeHistory")}
        />
      </Box>

      {/* Normal Screen Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={() => <div />}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Employee History */}
      <Box
        ref={reportRef}
        className="employee-history-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("employeeHistory")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* View Employee Dialog */}
      {showView && viewData && (
        <Dialog
          show={showView}
          onClose={handleCloseView}
          maxWidth="sm"
        >
          <Box sx={{ p: 3 }}>
            {/* Print Preview */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("employeeDetails")}
              />
            </Box>

            {/* Screen Employee Details */}
            <Box
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <EmployeeDetailsContent
                employee={viewData}
                t={t}
              />
            </Box>

            {/* Printable Employee Details */}
            <Box
              ref={detailsRef}
              className="employee-history-details-print"
              sx={{
                position: "absolute",
                left: "-10000px",
                top: 0,
                width: "1000px",
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <EmployeeDetailsContent
                employee={viewData}
                t={t}
              />
            </Box>

            {/* Close */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseView}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _get: actions.get,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(EmployeeHistory);
